import { useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import Tour from 'reactour';

import { MatterOverviewLoader } from './components/matter-overview-loader';
import { ProcessingPipeline } from './components/processing-pipeline';
import { RecentRunStatus } from './components/recent-run-status';
import { RecentSubsets } from './components/recent-subsets';
import { SubsetStatCard } from './components/subset-stat-card';
import { SuggestedSubset } from './components/suggested-subset';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { RunStatus } from '../../../../../../backend/src/types/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/shadcn/tooltip';
import { SubTitle } from '../../../components/subtitle';
import { cn } from '../../../lib/utils';
import { useGetMatterOverview } from '@/api/queries/useGetMatterOverview';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetRecentSubsets } from '@/api/queries/useGetRecentSubsets';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { useGetSummary } from '@/api/queries/useGetSummary';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRunOnAllDocsCase } from '@/helpers/runs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { MatterOverviewOnboarding } from '@/new/components/onboarding/matter-overview-V2-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { MATTER_OVERVIEW_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';
import { OverviewCreateChronologyModal } from '@/new/pages/matters/:matterId/subsets/:subsetId/components/dialogs/overview-create-chronology';
import { IssuesOverview } from '@/new/pages/matters/:matterId/subsets/:subsetId/components/issues-overview';
import { Summary } from '@/new/pages/matters/:matterId/subsets/:subsetId/components/summary';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function MatterOverview() {
  const { matterId } = useParams();
  if (!matterId) return;

  const { isOpen: isTourOpen, openTour, closeTour } = useTour('matter-overview');
  const [isCreateChronologyOpen, setIsCreateChronologyOpen] = useState(false);
  const { hasPermission } = useHasPermission();
  const canCreateUpload = hasPermission(Permission.MatterCreateUpload);

  const { data: matter, isLoading: isLoadingMatter } = useGetMatterV2(matterId);
  const allDocumentsSubsetId = matter?.allDocumentsCaseId ?? undefined;
  const { data: overview, isLoading: isLoadingOverview } = useGetMatterOverview(matterId);

  const { data: recentSubsets, isLoading: isLoadingRecentSubsets } = useGetRecentSubsets(matterId);
  const { data: runs, isLoading: isLoadingRuns } = useGetMatterRuns(matterId);
  const { data: subsets } = useGetSubsets(matterId);

  const { data: summaries, isLoading: summaryIsLoading } = useGetSummary(allDocumentsSubsetId);

  const isLoading = isLoadingMatter || isLoadingOverview || isLoadingRecentSubsets || isLoadingRuns;

  // Get processing state from store (computed in breadcrumb)
  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const hasCompletedRuns = processingState?.hasCompletedRuns ?? false;
  const hasProcessingRuns = hasAnyOngoingRunOnAllDocsCase(runs, allDocumentsSubsetId ?? '');
  // Use the first processing run (already sorted with awaiting approval first)
  const mostRecentProcessingRun = processingState?.allProcessingRuns?.[0] ?? null;
  const hasNoRuns = !isLoadingRuns && (!runs || runs.length === 0);

  if (isLoading) {
    return <MatterOverviewLoader />;
  }

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${matter.id}`,
                  label: matter.name,
                  active: true,
                  iconClass: 'lucide--folder',
                },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-4 px-0.5 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Icon className="lucide--calendar" />
                <div>Created {formatDate(matter?.createdDate ?? '')}</div>
              </div>

              <Link
                to={`/matters/${matterId}/settings/users`}
                className="flex cursor-pointer items-center gap-2 transition-colors hover:text-gray-700"
              >
                <Icon className="lucide--users-round" />
                <div>
                  <span className="font-semibold">{matter?.users?.length} </span>
                  {matter?.users?.length === 1 ? 'User' : 'Users'}
                </div>
              </Link>
            </div>

            <PageTitle text={matter?.name ?? ''} />
          </div>

          <div className="flex items-center gap-2">
            {processingState?.hasCompletedRuns && (
              <Button
                variant="primary"
                className={cn(hasProcessingRuns && 'pointer-events-none opacity-50')}
                asChild
                data-tour="create-subset-btn"
              >
                <Link to={`/matters/${matterId}/subsets/new`}>
                  <Icon className="lucide--group" />
                  Create Subset
                </Link>
              </Button>
            )}

            {canCreateUpload && (
              <Button
                variant="primary"
                className={cn(hasProcessingRuns && 'pointer-events-none opacity-50')}
                asChild
                data-tour="add-documents-btn"
              >
                <Link to={`/matters/${matterId}/docs/upload`}>
                  <Icon className="lucide--file-up" />
                  Upload Documents
                </Link>
              </Button>
            )}

            <Button variant="default" asChild>
              <Link to={`/matters/${matterId}/settings/metadata`}>
                <Icon className="lucide--settings" />
                Settings
              </Link>
            </Button>
          </div>
        </PageHeader>

        {runs && runs.length > 0 && (
          <div className="mb-4">
            <RecentRunStatus
              mostRecentRun={processingState?.mostRecentRun ?? null}
              allDocumentsCaseId={allDocumentsSubsetId}
            />
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-stretch gap-3" data-tour="matter-stats">
          <SubsetStatCard
            value={hasCompletedRuns ? overview?.totalFacts?.toLocaleString() : undefined}
            to={
              hasCompletedRuns && allDocumentsSubsetId
                ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/facts`
                : undefined
            }
          >
            <Icon className="lucide--search text-gray-500" />
            <p>Total Facts</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={hasCompletedRuns ? overview?.disputedFacts?.toLocaleString() : undefined}
            to={
              hasCompletedRuns && allDocumentsSubsetId
                ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/inconsistencies`
                : undefined
            }
          >
            <Icon className="lucide--alert-triangle text-gray-500" />
            <p>Inconsistencies</p>
          </SubsetStatCard>
          <SubsetStatCard
            value={hasCompletedRuns ? overview?.documents?.toLocaleString() : undefined}
            to={
              hasCompletedRuns && allDocumentsSubsetId
                ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/docs`
                : undefined
            }
          >
            <Icon className="lucide--file-text text-gray-500" />
            <p>Documents</p>
            {overview?.failedDocsCount !== 0 && allDocumentsSubsetId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/matters/${matterId}/subsets/${allDocumentsSubsetId}/docs?processed=false`}
                    className="flex items-center gap-1.5 text-red-500 transition-colors hover:text-red-600"
                  >
                    <Icon className="lucide--circle-alert" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {overview?.failedDocsCount} {overview?.failedDocsCount === 1 ? 'document' : 'documents'} in this
                  matter failed to process
                </TooltipContent>
              </Tooltip>
            )}
          </SubsetStatCard>
          <SubsetStatCard
            value={hasCompletedRuns ? overview?.entities.toLocaleString() : undefined}
            to={
              hasCompletedRuns && allDocumentsSubsetId
                ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/entities`
                : undefined
            }
          >
            <Icon className="lucide--users-round text-gray-500" />
            <p>Entities</p>
          </SubsetStatCard>

          {overview?.chronologies && overview?.chronologies > 0 ? (
            <SubsetStatCard
              value={hasCompletedRuns ? overview?.chronologies?.toLocaleString() : undefined}
              to={
                hasCompletedRuns && allDocumentsSubsetId
                  ? `/matters/${matterId}/subsets/${allDocumentsSubsetId}/chronologies`
                  : undefined
              }
            >
              <Icon className="lucide--git-fork text-gray-500" />
              <p>Chronologies</p>
            </SubsetStatCard>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 px-10 text-gray-500">
              <p>No Chronologies yet</p>
              <Button size="sm" disabled={!hasCompletedRuns} onClick={() => setIsCreateChronologyOpen(true)}>
                <Icon className="lucide--git-fork" />
                <span>Create your first Chronology</span>
              </Button>
            </div>
          )}
        </div>

        {hasCompletedRuns ? (
          <>
            {allDocumentsSubsetId && (
              <div className="mb-6">
                {(() => {
                  const hasSummary = Boolean(summaries?.content?.[0]?.content?.trim());
                  const issues = summaries?.content?.slice(1) ?? [];
                  const hasIssues = issues.length > 0 || summaryIsLoading;
                  return (
                    <>
                      {hasSummary && (
                        <Summary
                          summaryType="Matter"
                          summaryText={summaries?.content?.[0]?.content ?? ''}
                          isProcessing={hasAnyOngoingRunOnAllDocsCase(runs, allDocumentsSubsetId ?? '')}
                          isLoading={summaryIsLoading}
                          hasIssues={hasIssues}
                        />
                      )}
                      <IssuesOverview
                        issues={issues}
                        isLoading={summaryIsLoading}
                        matterId={matterId}
                        caseId={allDocumentsSubsetId}
                        hasSummary={hasSummary}
                      />
                    </>
                  );
                })()}
              </div>
            )}

            <div className="mb-6 flex gap-4" data-tour="recent-subsets">
              <div className="flex w-1/2 grow">
                <RecentSubsets recentlyAccessedSubsets={recentSubsets ?? []} subsets={subsets ?? []} />
              </div>
              <div className="w-1/2">
                <SuggestedSubset />
              </div>
            </div>
          </>
        ) : hasProcessingRuns && mostRecentProcessingRun && allDocumentsSubsetId ? (
          <div
            className="relative w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50/50 p-7"
            data-testid="processing-state"
          >
            <div>
              <SubTitle text="Matter Processing" className="mb-2" />
              <p className="text-sm leading-relaxed text-gray-600">
                Once processing is complete, you'll be able to explore your data.
              </p>
            </div>
            <div className="mt-4">
              <ProcessingPipeline
                casePipelineStatus={mostRecentProcessingRun.status as RunStatus}
                requiresAdminApproval={mostRecentProcessingRun.requiresAdminApproval}
              />
            </div>
          </div>
        ) : hasNoRuns ? (
          <div className="p-12">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon className="lucide--file-up" />
                </EmptyMedia>
                <EmptyTitle>No documents submitted</EmptyTitle>
                <EmptyDescription>
                  Upload and submit documents to start processing your matter. Once processing is complete, you'll be
                  able to explore your data.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : null}
      </PageContainer>

      <MatterOverviewOnboarding onTakeTour={openTour} />

      <Tour startAt={0} steps={MATTER_OVERVIEW_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />

      {allDocumentsSubsetId && (
        <OverviewCreateChronologyModal
          isOpen={isCreateChronologyOpen}
          onOpenChange={setIsCreateChronologyOpen}
          subsetId={allDocumentsSubsetId}
        />
      )}
    </>
  );
}
